/**
 * PedalCycleDetector: Detects pedal cycle phases (BDC/TDC) from knee angle oscillation.
 * 
 * Uses peak/trough detection on smoothed knee angle signal to identify:
 * - BDC (Bottom Dead Center): minimum knee flexion (max extension)
 * - TDC (Top Dead Center): maximum knee flexion (min extension)
 * 
 * Validates cycles based on amplitude, timing, and consistency.
 */

/**
 * Smooth a signal using moving average filter.
 * @param {number[]} values - Raw signal values
 * @param {number} windowSize - Moving average window size (default: 5)
 * @returns {number[]} - Smoothed signal
 */
export function smoothSignal(values, windowSize = 5) {
  if (values.length === 0) return [];
  if (windowSize <= 1) return [...values];
  
  const smoothed = [];
  const halfWindow = Math.floor(windowSize / 2);
  
  for (let i = 0; i < values.length; i++) {
    const start = Math.max(0, i - halfWindow);
    const end = Math.min(values.length - 1, i + halfWindow);
    const window = values.slice(start, end + 1);
    const sum = window.reduce((a, b) => a + b, 0);
    smoothed.push(sum / window.length);
  }
  
  return smoothed;
}

/**
 * Find local maxima (peaks) in a signal.
 * @param {number[]} signal - Signal values
 * @param {number} minProminence - Minimum peak prominence (default: 5)
 * @returns {number[]} - Array of peak indices
 */
export function findPeaks(signal, minProminence = 5) {
  if (signal.length < 3) return [];
  
  const peaks = [];
  
  for (let i = 1; i < signal.length - 1; i++) {
    const prev = signal[i - 1];
    const curr = signal[i];
    const next = signal[i + 1];
    
    // Check if it's a local maximum (handle plateaus by using >= for one side)
    if (curr >= prev && curr > next) {
      // Calculate prominence (how much it stands out)
      let leftMin = curr;
      let rightMin = curr;
      
      // Look left for minimum
      for (let j = i - 1; j >= 0 && signal[j] <= curr; j--) {
        leftMin = Math.min(leftMin, signal[j]);
      }
      
      // Look right for minimum
      for (let j = i + 1; j < signal.length && signal[j] <= curr; j++) {
        rightMin = Math.min(rightMin, signal[j]);
      }
      
      const prominence = Math.min(curr - leftMin, curr - rightMin);
      
      if (prominence >= minProminence) {
        peaks.push(i);
      }
    }
  }
  
  return peaks;
}

/**
 * Find local minima (troughs) in a signal.
 * @param {number[]} signal - Signal values
 * @param {number} minProminence - Minimum trough prominence (default: 5)
 * @returns {number[]} - Array of trough indices
 */
export function findTroughs(signal, minProminence = 5) {
  // Invert signal and find peaks, then return as troughs
  const inverted = signal.map(v => -v);
  return findPeaks(inverted, minProminence);
}

/**
 * Validate a single pedal cycle based on quality criteria.
 * @param {number} peakIdx - Index of peak (TDC)
 * @param {number} troughIdx - Index of trough (BDC)
 * @param {number[]} signal - Knee angle signal
 * @param {number[]} timestamps - Frame timestamps in ms
 * @returns {Object|null} - Cycle data if valid, null otherwise
 */
export function validateCycle(peakIdx, troughIdx, signal, timestamps) {
  const MIN_AMPLITUDE = 15; // degrees
  const MIN_CADENCE_RPM = 40; // slowest valid cadence
  const MAX_CADENCE_RPM = 120; // fastest valid cadence
  
  // Calculate amplitude
  const amplitude = Math.abs(signal[peakIdx] - signal[troughIdx]);
  
  if (amplitude < MIN_AMPLITUDE) {
    return null; // Not enough movement for a meaningful pedal stroke
  }
  
  // Calculate time duration between peak and trough
  const duration = Math.abs(timestamps[peakIdx] - timestamps[troughIdx]);
  
  // For half a pedal cycle (peak to trough or vice versa)
  // At 40 RPM: 1 revolution = 1500ms, half = 750ms
  // At 120 RPM: 1 revolution = 500ms, half = 250ms
  const minHalfCycleDuration = (60000 / MAX_CADENCE_RPM) / 2; // ~250ms
  const maxHalfCycleDuration = (60000 / MIN_CADENCE_RPM) / 2; // ~750ms
  
  if (duration < minHalfCycleDuration || duration > maxHalfCycleDuration) {
    return null; // Duration doesn't match valid pedaling cadence
  }
  
  return {
    peakIdx,
    troughIdx,
    amplitude,
    duration,
    peakValue: signal[peakIdx],
    troughValue: signal[troughIdx],
  };
}

/**
 * Detect complete pedal cycles from frame data.
 * @param {Array} frames - Frame data with angles and timestamps: [{ timestamp, angles: { knee, hip, ... } }]
 * @returns {Object} - Detected cycles and labeled frames
 */
export function detectPedalCycles(frames) {
  if (frames.length < 30) {
    return {
      cycles: [],
      labeledFrames: [],
      peakIndices: [],
      troughIndices: [],
      smoothedKnee: [],
    };
  }
  
  // Extract knee angles and timestamps
  const kneeAngles = frames.map(f => f.angles.knee);
  const timestamps = frames.map(f => f.timestamp);
  
  // Check for undefined/NaN values
  if (kneeAngles.some(v => v === undefined || isNaN(v))) {
    return {
      cycles: [],
      labeledFrames: [],
      peakIndices: [],
      troughIndices: [],
      smoothedKnee: [],
    };
  }
  
  // Smooth the knee angle signal
  const smoothedKnee = smoothSignal(kneeAngles, 5);
  
  // Find peaks (TDC - maximum knee flexion) with lower prominence for smoothed signal
  const peaks = findPeaks(smoothedKnee, 5);
  
  // Find troughs (BDC - minimum knee flexion)
  const troughs = findTroughs(smoothedKnee, 5);
  
  // Build complete cycles by pairing peaks with troughs
  const cycles = [];
  
  for (let i = 0; i < peaks.length; i++) {
    const peakIdx = peaks[i];
    
    // Find the trough before this peak
    const troughBefore = troughs.filter(t => t < peakIdx).pop();
    
    // Find the trough after this peak
    const troughAfter = troughs.find(t => t > peakIdx);
    
    if (troughBefore !== undefined) {
      const cycle = validateCycle(peakIdx, troughBefore, smoothedKnee, timestamps);
      if (cycle) {
        cycles.push({
          ...cycle,
          type: 'peak-to-trough',
          startIdx: troughBefore,
          endIdx: peakIdx,
        });
      }
    }
    
    if (troughAfter !== undefined) {
      const cycle = validateCycle(peakIdx, troughAfter, smoothedKnee, timestamps);
      if (cycle) {
        cycles.push({
          ...cycle,
          type: 'peak-to-trough',
          startIdx: peakIdx,
          endIdx: troughAfter,
        });
      }
    }
  }
  
  // Filter cycles for consistency (remove outliers in amplitude)
  if (cycles.length >= 3) {
    const amplitudes = cycles.map(c => c.amplitude);
    const meanAmplitude = amplitudes.reduce((a, b) => a + b, 0) / amplitudes.length;
    const filteredCycles = cycles.filter(c => {
      const deviation = Math.abs(c.amplitude - meanAmplitude) / meanAmplitude;
      return deviation < 0.3; // Within 30% of mean amplitude
    });
    
    cycles.length = 0;
    cycles.push(...filteredCycles);
  }
  
  // Label frames with pedal positions
  // Only label the EXACT peak/trough frames, not a range around them
  const labeledFrames = frames.map((frame, idx) => {
    let position = null;
    let cycleId = null;
    
    // Check if this frame is exactly at BDC (exact trough)
    const bdcIndex = troughs.indexOf(idx);
    if (bdcIndex !== -1) {
      position = 'BDC';
      cycleId = bdcIndex;
    }
    
    // Check if this frame is exactly at TDC (exact peak)
    const tdcIndex = peaks.indexOf(idx);
    if (tdcIndex !== -1) {
      position = 'TDC';
      cycleId = tdcIndex;
    }
    
    return {
      ...frame,
      pedalPosition: position,
      cycleId,
      frameIndex: idx,
    };
  });
  
  return {
    cycles,
    labeledFrames,
    peakIndices: peaks,
    troughIndices: troughs,
    smoothedKnee,
    totalCycles: cycles.length,
    validBDCFrames: troughs.length,
    validTDCFrames: peaks.length,
  };
}
